import React, { useState } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import GenericFileUpload from './GenericFileUpload';
import { uploadService } from '../../services/uploadService';

/**
 * Modal per creare un nuovo documento con upload file in un solo step
 */
const DocumentDataModal = ({ 
  show, 
  onHide, 
  onSuccess, 
  onError,
  userId 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    documentType: '',
    category: 'general'
  });
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = (uploadedFile, fileError) => {
    if (fileError) {
      setError(fileError?.message || fileError || 'Errore nel file');
      setFile(null);
    } else {
      setFile(uploadedFile);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validazione
    if (!formData.title.trim()) {
      setError('Il titolo è obbligatorio');
      return;
    }

    if (!file) {
      setError('Seleziona un file da caricare');
      return;
    }

    setUploading(true);

    try {
      // Prepara i dati per l'upload
      const uploadData = {
        ...formData,
        userId: userId,
        file: file
      };

      // Carica il documento usando uploadService
      const response = await uploadService.uploadDocument(uploadData);

      if (response.success) {
        onSuccess?.(response.data);
        handleClose();
      } else {
        setError(response.message || 'Errore nel caricamento del documento');
      }
    } catch (err) {
      console.error('Error creating document:', err);
      setError('Errore di connessione al server');
      onError?.(err);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      documentType: '',
      category: 'general'
    });
    setError('');
    setFile(null);
    setUploading(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Nuovo Documento</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Titolo Documento *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Inserisci il titolo del documento"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Categoria</Form.Label>
                <Form.Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value="general">Generale</option>
                  <option value="personal">Personale</option>
                  <option value="administrative">Amministrativo</option>
                  <option value="medical">Medico</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Tipo Documento</Form.Label>
            <Form.Control
              type="text"
              name="documentType"
              value={formData.documentType}
              onChange={handleInputChange}
              placeholder="es. Certificato, Contratto, Fattura..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descrizione</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Descrizione opzionale del documento..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>File Documento *</Form.Label>
            <GenericFileUpload
              onFileSelect={handleFileUpload}
              acceptedTypes={['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
              maxSize={10 * 1024 * 1024} // 10MB
            />
            {file && (
              <div className="mt-2 p-2 bg-light rounded">
                <i className="fas fa-file me-2"></i>
                {file.name} 
                <small className="text-muted">
                  ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </small>
                <Button
                  variant="link"
                  size="sm"
                  className="text-danger p-0 ms-2"
                  onClick={() => setFile(null)}
                  title="Rimuovi file"
                >
                  <i className="fas fa-times"></i>
                </Button>
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={handleClose}
            disabled={uploading}
          >
            Annulla
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            disabled={!file || !formData.title.trim() || uploading}
          >
            {uploading ? (
              <>
                <i className="fas fa-spinner fa-spin me-1"></i>
                Caricamento...
              </>
            ) : (
              <>
                <i className="fas fa-upload me-1"></i>
                Crea Documento
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DocumentDataModal;